import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnChanges,
  OnDestroy,
} from '@angular/core';
import { take } from 'rxjs/operators';
import { DisplayItem } from '../production-cost.component';
import {
  CostItem,
  CostItemService,
  EnumService,
  ModalService,
  Position,
  CostItemComment,
  ItemGroupComment,
  LocalStorageService,
  UsersService,
  SessionManagerService,
  ProjectStatusGuardService,
  CostItemCommentService,
} from '@ops/shared-types';
import { OpsCustomCurrencyModalComponent, CustomCurrencyModalConfig } from '@ops/storybook';
import { CommonModule } from '@angular/common';
import {
  CommentPopupModule,
  DocumentModalModule,
  DropdownModule,
  EnterPressDirective,
  OpsNumberDirective,
  ActionMenuComponent,
  ActionMenuDirective,
  ActionMenuService,
  ActionMenuOption,
  NumericInputComponent,
} from '@ops/components';
import { FormsModule } from '@angular/forms';
import { OpsCalendarComponent, OpsSelectComponent } from '@ops/storybook';
import { Miscellanious } from '@ops/global-config';
import { debounce } from 'lodash';
import { Subscription } from 'rxjs';
import { ProductionCostService } from '../production-cost.service';
import { differenceInCalendarDays, differenceInDays } from 'date-fns';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

export interface NameIdPair {
  id: string;
  name: string;
}

@Component({
  selector: 'ops-cost-accordion-item',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DocumentModalModule,
    DropdownModule,
    CommentPopupModule,
    EnterPressDirective,
    OpsNumberDirective,
    OpsCalendarComponent,
    OpsSelectComponent,
    TranslateModule,
    ActionMenuComponent,
    ActionMenuDirective,
    OpsCustomCurrencyModalComponent,
    NumericInputComponent,
  ],
  templateUrl: './cost-accordion-item.component.html',
  styleUrls: ['./cost-accordion-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CostAccordionItemComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() item: DisplayItem;
  @Input() companyId: string;
  @Input() selectedItemsCount: number;
  @Input() saveFlag: boolean;
  @Input() cancelFlag: boolean;
  @Input() selectAllFlag: boolean;
  @Input() costTypeInput: string;
  @Input() positionInput: string;
  @Input() nameInput: string;
  @Input() descriptionInput: string;
  @Input() daysInput: number;
  @Input() unitsInput: number;
  @Input() unitCostInput: number;
  @Input() paymentMethodInput: string;
  @Input() paymentDateInput?: Date;
  @Input() paidByInput: string;
  @Input() taxPercentageInput: number;
  @Input() costStatusInput: string;
  @Input() dateOfBillInput?: Date;
  @Input() assignToInput: string;
  @Input() currencyInput: string;
  @Input() exchangeRateInput: number;
  @Input() accountingCategoryInput: string;
  @Input() hasReminderInput: boolean;
  @Input() positions: Array<Position>;
  @Input() itemGroupTitle: string;
  @Input() refreshTrigger?: boolean;
  @Output() itemToggled: EventEmitter<number> = new EventEmitter<number>();
  @Output() recalculateTotal: EventEmitter<CostItem> =
    new EventEmitter<CostItem>();
  @Output() itemDeleted: EventEmitter<CostItem> = new EventEmitter<CostItem>();
  @Output() duplicateItem: EventEmitter<CostItem> =
    new EventEmitter<CostItem>();
  @Output() moveItem: EventEmitter<DisplayItem> = new EventEmitter<DisplayItem>();
  @Output() addNewPaidByOption: EventEmitter<{ type: string }> = new EventEmitter<{ type: string }>();


  multiselected = false;
  shouldEnableOnlyCommentsFeature: boolean = false;
  costTypeEdit?: string;
  positionEdit?: string;
  nameEdit?: string;
  descriptionEdit?: string;
  daysEdit?: number;
  unitEdit?: number;
  unitCostEdit?: number;
  taxPercentageEdit?: number;
  paidByEdit?: string;
  paymentMethodEdit?: string;
  showAddNewOption?: boolean;
  paymentDateEdit?: Date;
  taxEdit?: boolean;
  costStatusEdit?: string;
  dateOfBillEdit?: Date;
  assignToEdit?: string;
  accountingCategoryEdit?: string;
  hasReminderEdit?: boolean;
  currencyEdit?: string;
  exchangeRateEdit?: number;
  exchangeRate?: string;

  units: string;
  days: string;
  cost: string;

  isTaxed: boolean;

  paymentDateString?: string;
  dateOfBillString?: string;

  confirmed?: boolean;

  isExpanded = false;

  isReadOnly = false;
  isFullPrevention = false;

  paymentMethodOptions: Array<string>;
  paidByOptions: Array<string>;
  costTypeOptions: Array<string>;
  costStatusOptions: Array<string>;
  assignToOptions: Array<string>;
  accountingCategoryOptions: Array<string>;
  positionOptions: Array<string>;
  currencyOptions: Array<string>;

  // Custom currency properties
  showCustomCurrencyForm: boolean = false;
  customCurrencyConfig: CustomCurrencyModalConfig = {
    excludeCurrencyCodes: [],
    excludeReason: 'This currency is already in use',
    onSuccess: {
      updateField: 'currencyEdit',
      additionalData: { refreshOptions: true }
    }
  };

  assignToValues: Array<NameIdPair>;

  dateFormat = 'dd.MM.yyyy';

  companyCurrencyCode?: string;

  searchPosition: string;

  currentOpenModal: string;

  paidByNameIdList: Array<NameIdPair>;

  constructor(
    private dataService: CostItemService,
    private modalService: ModalService,
    private enumService: EnumService,
    private changeDetector: ChangeDetectorRef,
    private usersService: UsersService,
    private sessionManagerService: SessionManagerService,
    private localStorageService: LocalStorageService,
    private productionCostService: ProductionCostService,
    private translateService: TranslateService,
    private actionMenuService: ActionMenuService,
    private projectStatusGuard: ProjectStatusGuardService,
    private costItemCommentService: CostItemCommentService
  ) {
    this.companyCurrencyCode =
      this.localStorageService.getCurrentCompany()?.baseCurrencyCode;
    this.shouldEnableOnlyCommentsFeature = this.projectStatusGuard.shouldEnableOnlyCommentsFeature(this.projectStatusGuard.getCurrentProject());
  }
  ngOnDestroy(): void {
    if (this.subUserEditedBy?.unsubscribe) {
      this.subUserEditedBy.unsubscribe();
    }
    if (this.subPreventionStatus?.unsubscribe) {
      this.subPreventionStatus.unsubscribe();
    }
    if (this.subFullPreventionStatus?.unsubscribe) {
      this.subFullPreventionStatus.unsubscribe();
    }
  }

  async ngOnInit() {
    // Set initial prevention status
    this.isReadOnly = this.projectStatusGuard.getProjectStatusPrevention();
    this.isFullPrevention = this.projectStatusGuard.getProjectStatusFullPrevention();
    
    // Subscribe to prevention status changes for reactive updates
    this.subPreventionStatus = this.projectStatusGuard.getProjectStatusPrevention$().subscribe(
      (shouldPrevent: boolean) => {
        this.isReadOnly = shouldPrevent;
        this.changeDetector.markForCheck();
      }
    );
    
    this.subFullPreventionStatus = this.projectStatusGuard.getProjectStatusFullPrevention$().subscribe(
      (shouldFullPrevent: boolean) => {
        this.isFullPrevention = shouldFullPrevent;
        this.changeDetector.markForCheck();
      }
    );
    
    this.isExpanded = this.item.isExpanded ?? false;

    this.cancelChanges();

    if (this.item.paymentDate) {
      this.item.paymentDate = new Date(this.item.paymentDate);
    }

    if (this.item.paymentMethod === 'Credit Card' || this.item.paymentMethod === 'Bank Transfer') {
      this.showAddNewOption = true;
    }

    if (this.isExpanded) {
      await this.getEnums();
    }
  }

  async getAllPaidByOptions() {
    // ✅ CHANGED: Use all payment method options (unfiltered) to show all methods
    // This allows users to see all payment methods even if no accounts/cards are configured
    this.paymentMethodOptions = this.enumService.getAllPaymentMethodOptions();
  }

  //TODO: consider using RxJS instead of this, performance issues
  ngOnChanges(changes: SimpleChanges) {
    if (changes.refreshTrigger) {
      this.setPaidByOptions(this.paymentMethodEdit, () => {});
    }
    
    // ✅ FIXED: Refresh paid-by options when company changes
    if (changes.companyId && !changes.companyId.isFirstChange()) {
      // console.log('CostAccordionItem: Company changed, refreshing paid-by options', {
      //   oldCompanyId: changes.companyId.previousValue,
      //   newCompanyId: changes.companyId.currentValue,
      //   paymentMethod: this.paymentMethodEdit || this.item.paymentMethod
      // });
      this.refreshPaidByOptionsForNewCompany();
    }
    if (changes.selectAllFlag) {
      this.multiselected = changes.selectAllFlag.currentValue;
    }
    if (this.multiselected) {
      if (changes.saveFlag) {
        this.isExpanded = false;
        this.onSaveChanges();
      }
      if (changes.cancelFlag && !changes?.cancelChanges?.isFirstChange) {
        this.isExpanded = false;
        this.multiselected = false;
        this.cancelChanges();
      }

      if (changes.costTypeInput && this.costTypeInput) {
        this.costTypeEdit = this.costTypeInput;
      }
      if (changes.positionInput && this.positionEdit) {
        this.positionEdit = this.positionInput;
      }
      if (changes.nameInput && this.nameEdit) {
        this.nameEdit = this.nameInput;
      }
      if (changes.descriptionInput && this.descriptionEdit) {
        this.descriptionEdit = this.descriptionInput;
      }
      if (changes.daysInput && this.daysInput) {
        this.daysEdit = this.daysInput;
        this.days = this.daysInput.toString();
      }
      if (changes.exchangeRateInput && this.exchangeRate) {
        this.exchangeRateEdit = this.exchangeRateInput;
        this.exchangeRate = this.exchangeRateInput.toString();
      }
      if (changes.unitsInput && this.unitsInput) {
        this.unitEdit = this.unitsInput;
        this.units = this.unitsInput.toString();
      }
      if (changes.unitCostInput) {
        if (this.unitCostInput) {
          this.unitCostEdit = this.unitCostInput;
          this.cost = this.unitCostInput.toString();
        } else {
          this.unitCostEdit = this.item.cost;
          this.cost = this.item.cost.toString();
        }
      }
      if (changes.paidByInput && !changes.paidByInput.isFirstChange) {
        if (this.paidByInput) this.paidByEdit = this.paidByInput;
        else this.paidByEdit = this.item.paidBy;
      }
      if (
        changes.paymentMethodInput &&
        !changes.paymentMethodInput.isFirstChange
      ) {
        if (this.paymentMethodInput) {
          this.onSelectPaymentMethod(this.paymentMethodInput);
        } else this.onSelectPaymentMethod(this.item.paymentMethod);
      }
      if (changes.costStatusInput && this.costStatusInput) {
        this.costStatusEdit = this.costStatusInput;
      }
      if (changes.accountingCategoryInput) {
        if (this.accountingCategoryInput)
          this.accountingCategoryEdit = this.accountingCategoryInput;
        else this.accountingCategoryEdit = this.item.accountingCategory;
      }
      if (changes.assignToInput) {
        if (this.assignToInput) this.assignToEdit = this.assignToInput;
        else this.assignToEdit = this.item.assignedTo;
      }
      if (changes.currencyInput) {
        if (this.currencyInput) this.currencyEdit = this.currencyInput;
        else this.currencyEdit = this.item.currencyCode;
      }
      if (
        changes.paymentDateInput &&
        !changes.paymentDateInput.isFirstChange()
      ) {
        this.paymentDateEdit = this.paymentDateInput
          ? new Date(this.paymentDateInput)
          : null;
      }
      if (changes.dateOfBillInput && !changes.dateOfBillInput.isFirstChange()) {
        this.dateOfBillEdit = this.dateOfBillInput
          ? new Date(this.dateOfBillInput)
          : null;
      }
      if (
        changes.taxPercentageInput &&
        !changes.taxPercentageInput.isFirstChange()
      ) {
        if (this.taxPercentageInput != undefined)
          this.taxPercentageEdit = this.taxPercentageInput;
      }
      if (
        changes.hasReminderInput &&
        !changes.hasReminderInput.isFirstChange()
      ) {
        if (this.hasReminderInput != undefined)
          this.hasReminderEdit = this.hasReminderInput;
        else this.hasReminderEdit = this.item.hasReminder;
      }
    }
  }

  setPaidByEditString = (): void => {
    this.paidByEdit = this.paidByNameIdList?.find(
      (p) => p.id === this.item.paidBy
    )?.name;
    this.changeDetector.markForCheck();
  };

  setAssignedToEditString = (): void => {
    this.assignToEdit = this.assignToValues?.find(
      (p) => p.id === this.item.assignedTo
    )?.name;
    this.changeDetector.markForCheck();
  };

  openModal(id: string, event: any) {
    this.closeModal(this.currentOpenModal);

    if (!this.isExpanded) return;
    event.stopPropagation();
    this.modalService.open(id);

    this.currentOpenModal = id;
  }

  openComments(id: string, event?: any) {
    event?.stopPropagation();
    if (!this.item?.comments?.length) {
      this.costItemCommentService
        .getCostItemComments(this.item._id, this.item.project)
        .pipe(take(1))
        .subscribe(
          (comments: Array<CostItemComment>) => {
            this.item.comments = comments
            this.modalService.open(id, comments);
          }
        );
    } else {
      this.modalService.open(id, this.item.comments);
    }
    // this.modalService.open(id);
  }

  closeModal(id: string) {
    this.modalService.close(id);
  }

  manageFiles() {
    
    this.modalService.open('document-modal' + this.item._id, {readonly: this.projectStatusGuard.getProjectStatusFullPrevention()} );
  }

  onHandleDocumentClick(e) {
    e.stopPropagation();

    this.manageFiles();
  }

  onToggleSelection(event?: any) {
    event?.stopPropagation();
    this.cancelChanges();
    if (!this.multiselected) {
      this.itemToggled.emit(1);
    } else {
      this.itemToggled.emit(-1);
    }
    this.multiselected = !this.multiselected;
    this.isExpanded = this.multiselected;
  }

  //try using CostItemObjects
  saveChanges() {
    //add missing
    this.item.position = this.positionEdit;
    this.item.name = this.nameEdit;
    this.item.description = this.descriptionEdit;
    this.item.days = +this.daysEdit.toString().replaceAll(',', '');
    this.item.units = +this.unitEdit.toString().replaceAll(',', '');
    this.item.cost = +this.unitCostEdit.toString().replaceAll(',', '');
    this.item.taxPercentage = +this.taxPercentageEdit;
    this.item.paymentMethod = this.paymentMethodEdit;
    this.item.paidBy =
      this.paidByNameIdList?.find((p) => p.name === this.paidByEdit)?.id ??
      null;

    this.item.paymentDate = this.paymentDateEdit
      ? new Date(this.paymentDateEdit)
      : null;
    this.item.costType = this.costTypeEdit;
    this.item.dateOfBill = this.dateOfBillEdit
      ? new Date(this.dateOfBillEdit)
      : null;
    this.item.costStatus = this.costStatusEdit;
    this.item.accountingCategory = this.accountingCategoryEdit;
    this.item.currencyCode = this.currencyEdit;
    this.item.exchangeRate = this.exchangeRateEdit;
    this.item.hasReminder = this.hasReminderEdit ? this.hasReminderEdit : false;

    this.item.assignedTo =
      this.assignToValues.find((a) => a.name === this.assignToEdit)?.id || null;

    this.dataService
      .updateCostItem(this.item, this.item.project)
      .pipe(take(1))
      .subscribe(
        () => {
          this.item.paidBy = this.paidByNameIdList?.find(
            (p) => p.name === this.paidByEdit
          )?.id;
          this.recalculate();
          // this.toggleExpand();
        },
        (err) => window.alert(this.translateService.instant('windowAlert.costAccordionItem.httpError', { error: err }))
      );
  }

  cancelChanges() {
    this.positionEdit = this.item.position;
    this.searchPosition = this.item.position;
    this.nameEdit = this.item.name;
    this.descriptionEdit = this.item.description;
    this.daysEdit = this.item.days;
    this.days = this.item.days.toString();
    this.unitEdit = this.item.units;
    this.units = this.item.units.toString();
    this.unitCostEdit = this.item.cost;
    this.cost = this.item.cost.toString();
    this.taxPercentageEdit = this.item.taxPercentage;
    this.isTaxed = !!this.item.taxPercentage;
    this.onSelectPaymentMethod(this.item.paymentMethod);
    this.setPaidByEditString();
    this.setAssignedToEditString();
    this.costStatusEdit = this.item.costStatus;
    this.accountingCategoryEdit = this.item.accountingCategory;
    this.hasReminderEdit = this.item.hasReminder;
    this.paymentDateEdit = this.item.paymentDate;
    this.paymentDateString = '';
    this.dateOfBillEdit = this.item.dateOfBill;
    this.dateOfBillString = '';
    this.currencyEdit = this.item.currencyCode;
    this.exchangeRateEdit = this.item.exchangeRate;

    this.exchangeRate = this.item.exchangeRate?.toString();
    if (this.item.paymentDate) {
      this.paymentDateEdit = new Date(this.item.paymentDate);
      this.paymentDateString =
        ('0' + this.paymentDateEdit?.getDate()).slice(-2) +
        '.' +
        ('0' + (this.paymentDateEdit?.getMonth() + 1)).slice(-2) +
        '.' +
        this.paymentDateEdit?.getFullYear();
    }

    this.costTypeEdit = this.item.costType;
    if (this.item.dateOfBill) {
      this.dateOfBillEdit = new Date(this.item.dateOfBill);
      this.dateOfBillString =
        ('0' + this.dateOfBillEdit?.getDate()).slice(-2) +
        '.' +
        ('0' + (this.dateOfBillEdit?.getMonth() + 1)).slice(-2) +
        '.' +
        this.dateOfBillEdit?.getFullYear();
    }

    this.changeDetector.markForCheck();
  }

  recalculate() {
    this.item.total =
      this.item.days *
      this.item.units *
      this.item.cost *
      (1 + this.item.taxPercentage / 100);
    this.recalculateTotal.emit(this.item);
  }

  onCancelChanges() {
    this.closeModal(this.currentOpenModal ?? '');
    this.cancelChanges();
    this.multiselected = false;
    this.itemToggled.emit(-1);
    this.isExpanded = false;
    this.changeDetector.markForCheck();
  }

  onSaveChanges() {
    this.closeModal(this.currentOpenModal ?? '');
    this.saveChanges();

    this.item.editedBy = this.sessionManagerService.getUser()._id;
    this.item['updatedAt'] = new Date();
    this.onToggleSelection();
    this.isExpanded = false;
  }

  onSelectCostType(selection: string) {
    this.costTypeEdit = selection;
  }

  onTypeSearchPosition = debounce((event: any) => {
    this.searchPosition = event.target.value;

    if (this.searchPosition.length > 1) {
      this.positionOptions = this.positions
        .filter((p) =>
          p?.name?.toUpperCase().includes(this.searchPosition?.toUpperCase())
        )
        .map((p) => p.abbreviation || p.name);
      this.positionOptions.unshift(this.translateService.instant('+ ADD NEW'));
      this.changeDetector.markForCheck();
      return;
    }
    this.positionOptions = this.positions.map((p) => p.abbreviation || p.name);
  }, 500);

  onSelectPosition(selection: string) {
    if (selection === this.translateService.instant('+ ADD NEW')) {
      this.positionEdit = this.searchPosition;
      return;
    }
    this.positionEdit = selection;
    this.searchPosition = selection;
  }

  onEditName(event: any) {
    this.nameEdit = event.target.value;
  }
  onEditDescription(event: any) {
    this.descriptionEdit = event.target.value;
  }
  onEditDays(event: any) {
    this.daysEdit = +event.target.value.toString().replaceAll(',', '');
  }
  onEditUnit(event: any) {
    this.unitEdit = +event.target.value.toString().replaceAll(',', '');
  }
  onEditCost(event: any) {
    this.unitCostEdit = +event.target.value.toString().replaceAll(',', '');
  }
  onToggleTax() {
    if (this.taxPercentageEdit) {
      this.isTaxed = false;
      this.taxPercentageEdit = 0;
      return;
    }
    this.taxPercentageEdit = 10;
    this.isTaxed = !this.isTaxed;
  }
  onEditTax(event: any) {
    this.taxPercentageEdit = +event.target.value;
  }
  onSelectPaymentMethod(value: any) {
 

    this.showAddNewOption = value === 'Credit Card' || value === 'Bank Transfer';
    this.paymentMethodEdit = value;
    this.paidByEdit = null;
    this.changeDetector.markForCheck();
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    if (this.isExpanded) {
      this.setPaidByOptions(value, () => {});
    }
  }
  onSelectpaidBy(value: any) {
    if (value === 'Add New') {
      // Emit event to parent component to handle modal opening
      if (this.paymentMethodEdit === 'Credit Card') {
        this.addNewPaidByOption.emit({ type: 'CREDIT_CARD' });
      } else if (this.paymentMethodEdit === 'Bank Transfer') {
        this.addNewPaidByOption.emit({ type: 'BANK_TRANSFER' });
      }
      this.changeDetector.markForCheck();
      return;
    }
    this.paidByEdit = value;
  }
  onSelectPaymentDate(value: any) {
    this.paymentDateEdit = value;
  }
  onSelectCostStatus(selection: string) {
    this.costStatusEdit = selection;
  }
  onSelectdateOfBill(selection: Date) {
    this.dateOfBillEdit = selection;
    if (!this.paymentDateEdit) {
      this.onSelectPaymentDate(selection);
    }
  }
  onSelectAssignTo(selection: string) {
    this.assignToEdit = selection;
  }
  onSelectAccountingCategory(selection: string) {
    this.accountingCategoryEdit = selection;
  }

  onSelectCurrency(selection: string) {
    if (selection === '+ Add New') {
      this.toggleCustomCurrencyForm();
      return;
    }
    
    this.currencyEdit = selection;
  }
  onExchangeRateInput(event: number | null) {
    this.exchangeRateEdit = event ?? 0;
  }

  calculateReciprocal(): void {
    if (this.exchangeRateEdit && this.exchangeRateEdit !== 0) {
      this.exchangeRateEdit = 1 / this.exchangeRateEdit;
      this.onExchangeRateInput(this.exchangeRateEdit);
    }
  }

  onToggleReminder(event: any) {
    event.stopPropagation();
    this.hasReminderEdit = !this.hasReminderEdit;
  }

  onSaveComment(comment: CostItemComment) {
    if (!comment._id) {
      this.costItemCommentService
        .createCostItemComment(this.item._id, comment.text, this.item.project)
        .subscribe((c: CostItemComment) => {
          this.item.comments.push({
            _id: c._id,
            createdAt: new Date(),
            text: comment.text,
          });
          this.item.hasComment = true;
          this.modalService.open(`comments-popup-${this.item._id}`, this.item.comments);
        });
      // , (errorMessage) => {
      //   //TODO delete this monstrosity
      //   this.item.comments.push({_id: 'id', createdAt: new Date(), text: comment.text});
      //     this.item.hasComment = true;
      // }
      return;
    }
    this.costItemCommentService
      .updateCostItemComment(comment._id, comment.text, this.item.project)
      .subscribe((c: CostItemComment  )=>{
        this.item.comments = this.item.comments.map((c)=>c._id === c._id ? c : c);
        this.modalService.open(`comments-popup-${this.item._id}`, this.item.comments);
      });
  }

  onDeleteComment(commentId: string) {
    this.costItemCommentService
      .deleteCostItemComment(commentId, this.item.project)
      .subscribe(() => {
        this.item.comments = this.item.comments.filter(
          (c) => c._id != commentId
        );
        if (this.item.comments.length < 1) {
          this.item.hasComment = false;
        } 

        this.modalService.open(`comments-popup-${this.item._id}`, this.item.comments);
      });
  }

  onDeleteItem(event?: any) {
    event?.stopPropagation();
    const param1 = this.item.position ?  this.translateService.instant(this.item.position) : '';
    const param2 = this.item.name ? this.translateService.instant(this.item.name) : '';
    if (
      confirm(
        this.translateService.instant('windowConfirm.costAccordionItem.deleteCostItem', {
          position: param1,
          name: param2
        })
      )
    ) {
      this.dataService
        .deleteCostItem(this.item._id, this.item.project)
        .subscribe(() => {
          if (this.isExpanded || this.multiselected) {
            this.onToggleSelection();
          }
          this.itemDeleted.emit(this.item);
        });
    }
  }

  onDuplicateItem() {
    this.duplicateItem.emit(this.item);
  }

  toggleExpand() {
    this.isExpanded = !this.isExpanded;
    if (this.isExpanded != this.multiselected) {
      this.onToggleSelection();
    }
  }

  async expandItem() {
    if (!this.isExpanded) {
      this.toggleExpand();
      await this.getEnums();
    }
  }

  stopPropagation(event) {
    event.stopPropagation();
  }

  editedByFullName?: string;
  subUserEditedBy?: Subscription;
  subPreventionStatus?: Subscription;
  subFullPreventionStatus?: Subscription;

  async getAndSetUserEditedBy() {
    this.subUserEditedBy = await this.usersService
      .getUserById(this.item.editedBy)
      .subscribe((userFullName) => {
        this.editedByFullName = userFullName;
      });
  }



  onFilesUploaded(item: any) {
    this.item.documents = item.documents;
  }

  setPaidByOptions(paymentMethod: string, callback?: () => void) {
    if (!paymentMethod) return;
    
    // console.log('CostAccordionItem: setPaidByOptions called', {
    //   paymentMethod,
    //   availableMethods: this.productionCostService.paidByOptions.map(o => o.method),
    //   totalPaidByOptions: this.productionCostService.paidByOptions.length
    // });
    
    const data = this.productionCostService.paidByOptions.find(
      (o) => o.method === paymentMethod
    );
    
    // console.log('CostAccordionItem: Found data for payment method', {
    //   paymentMethod,
    //   data: data ? { method: data.method, optionsCount: data.optionsList.length } : null
    // });
    
    this.paidByNameIdList = data?.pairList || [];
    this.paidByOptions = data?.optionsList || [];
    this.changeDetector.markForCheck();
    callback && callback();
  }

  // ✅ FIXED: Method to refresh paid-by options when company changes
  private async refreshPaidByOptionsForNewCompany(): Promise<void> {
    try {
      // console.log('CostAccordionItem: Starting refresh for new company');
      
      // Wait for the production cost service to refresh its data
      await this.productionCostService.refreshPaidByOptions();
      
      // console.log('CostAccordionItem: Production cost service refreshed, now updating component');
      
      // If we have a payment method selected, refresh its options
      if (this.paymentMethodEdit) {
        // console.log('CostAccordionItem: Refreshing with paymentMethodEdit:', this.paymentMethodEdit);
        this.setPaidByOptions(this.paymentMethodEdit, () => {});
      } else if (this.item.paymentMethod) {
        // console.log('CostAccordionItem: Refreshing with item.paymentMethod:', this.item.paymentMethod);
        this.setPaidByOptions(this.item.paymentMethod, () => {});
      }
      
      // console.log('CostAccordionItem: Paid-by options refreshed for new company');
    } catch (error) {
      console.error('Error refreshing paid-by options for new company:', error);
    }
  }

  private async getEnums() {
    //await this.productionCostService.setAssignToValues();
    await this.getAllPaidByOptions();
    if (this.item.paymentMethod) {
      //TODO: refactor to include this in the costItem/companyitem model or service?
      this.setPaidByOptions(this.item.paymentMethod, this.setPaidByEditString);
      this.paidByEdit = this.paidByNameIdList.find(
        (o) => o.id === this.item.paidBy
      )?.name;
    }
    this.accountingCategoryOptions =
      this.enumService.getAccountingCategoryOptions();
    this.costTypeOptions = this.enumService.getCostTypeOptions();
    // this.paymentMethodOptions = this.enumService.getPaymentMethodOptions();
    this.assignToValues = this.productionCostService.assignToValues;
    this.setAssignedToEditString();
    this.assignToOptions = this.assignToValues.map((a) => a.name);
    this.costStatusOptions = this.enumService.getCostStatuseOptions();
    this.currencyOptions = [
      '+ Add New', // Add custom currency option
      ...this.enumService.getCurrencyOptions()
    ];

    this.positionOptions = this.positions.map((p) => p.name);

    if (!this.positionOptions.includes(this.item.position)) {
      this.positionOptions.unshift(this.item.position);
    }
  }

  get estimatedTotalExchange() {
    if (!this.item.estimatedTotal) return 0;
    return this.item.estimatedTotal * this.item.estimatedExchangeRate;
  }

  get totalExchange() {
    if (!this.item.total) return 0;
    return this.item.total * this.exchangeRateEdit;
  }

  get currencySymbol() {
    if (!this.currencyEdit || this.currencyEdit === '+ Add New') {
      return '';
    }
    return Miscellanious.currencySymbolFromCode('en-US', this.currencyEdit);
  }

  get currencyExchangeSymbol() {
    return Miscellanious.currencySymbolFromCode(
      'en-US',
      this.companyCurrencyCode
    );
  }

  get itemCurrencySymbol() {
    return Miscellanious.currencySymbolFromCode(
      'en-US',
      this.item.estimatedCurrencyCode
    );
  }

  get reminderClassName(): string {
    if (!this.item?.hasReminder || !this.item?.dateOfBill) return '';

    const dayDiff = differenceInCalendarDays(
      new Date(this.item.dateOfBill),
      new Date()
    );

    if (dayDiff > 5) {
      return 'grey-icon';
    }
    if (dayDiff > 1 && dayDiff <= 5) {
      return 'orange-icon';
    }

    return 'red-icon';
  }

  get hasDifferentCurrency() {
    if (!this.currencyEdit) return false;
    if (this.currencyEdit && this.companyCurrencyCode) {
      if (this.currencyEdit !== this.companyCurrencyCode) {
        return true;
      }
    }
    return false;
  }

  getItemActionMenuOptions(): ActionMenuOption[] {
    const options: ActionMenuOption[] = [
      {
        label: 'productionCost.costAccordionItem.comment',
        value: 'comment'
      },
      {
        label: 'productionCost.costAccordionItem.duplicate',
        value: 'duplicate'
      },
      {
        label: 'productionCost.costAccordionItem.moveItemToDepartment',
        value: 'moveItem'
      },
      {
        label: 'productionCost.costAccordionItem.manageFiles',
        value: 'manageFiles'
      },
      {
        label: 'productionCost.costAccordionItem.delete',
        value: 'delete',
        isDelete: true
      }
    ];

    return options;
  }



  onItemActionSelected(action: string) {
    switch (action) {
      case 'comment':
        this.openComments(`comments-popup-${this.item._id}`);
        break;
      case 'duplicate':
        this.onDuplicateItem();
        break;
      case 'moveItem':
        this.onMoveItem();
        break;
      case 'manageFiles':
        this.manageFiles();
        break;
      case 'delete':
        this.onDeleteItem();
        break;
    }
  }

  onMoveItem() {
    // console.log('onMoveItem FROM COST ACCORDION ITEM', this.item);
    this.moveItem.emit(this.item);
  }

  // Custom Currency Methods
  toggleCustomCurrencyForm(): void {
    this.showCustomCurrencyForm = !this.showCustomCurrencyForm;
  }

  onCustomCurrencyAdded(event: { 
    currencyCode: string; 
    updateField?: string; 
    additionalData?: any; 
  }): void {
    // Refresh currency options to include the new custom currency
    this.currencyOptions = [
      '+ Add New', // Add custom currency option
      ...this.enumService.getCurrencyOptions()
    ];
    
    // Auto-select the newly added currency
    this.currencyEdit = event.currencyCode;
    
    // Close the modal
    this.showCustomCurrencyForm = false;
  }

  onCustomCurrencyModalClosed(): void {
    this.showCustomCurrencyForm = false;
  }
}
